import { createApp, h, reactive } from 'vue';
import { createPinia } from 'pinia';
import Axios from './config/http';
import csrf from './config/csrf';
import { setHttpClient } from './config/httpClient';
import router from './app/router';
import store from './app/store';
import { createRoot } from './app/root.js';
import { installTrainingGuard, settleTrainingGate } from './app/router/trainingGuard.js';
import { initTelemetry } from './config/telemetry';
import loginUrl from './config/loginUrl';
import { isTrainingStart, startTraining } from './config/trainingStart';

// Spec 53 section 5.3: the practice fleet's way in. It arrives with no
// session, so it is handled before the session check below, and the app does
// not boot at that address at all (config/trainingStart.js).
const trainingStart = isTrainingStart();
if (trainingStart) startTraining();

let csrf_token = null;

try {
  if (!trainingStart) csrf_token = csrf.getToken();
} catch (e) {
  window.location = loginUrl();
}
const axios = Axios(csrf_token);
setHttpClient(axios);

// What the root renders is decided by the /users/me round trip below. It is a
// module-level reactive rather than root `data` so it can be set BEFORE the
// app mounts; App declares `user` as a required prop and must never see null.
const boot = reactive({ user: null, error: false });

// audit_web.md 3.8: a boot failure used to render a bare unstyled <h1>Error</h1>
// in an inline-styled div. It now paints the same .app-boot markup
// ui/index.html shows statically before this file runs, with the --error
// modifier (app.scss) that hides the spinner: nothing is loading any more, so
// nothing should imply it still is. role="alert" because this is the one boot
// state that should interrupt rather than sit quietly (LwNotice's danger tone
// makes the same call for the same reason). Render functions rather than a
// template string: the runtime-only Vue build has no in-browser compiler.
function renderBootError() {
  return h('div', { class: 'app-boot app-boot--error', role: 'alert' }, [
    h('div', { class: 'app-boot__spinner', 'aria-hidden': 'true' }),
    h('p', { class: 'app-boot__label' }, 'Something went wrong'),
    h(
      'p',
      { class: 'app-boot__detail' },
      'There was an error loading the app: the API service appears to not be available.'
    ),
  ]);
}

// The shell, or for a gated user the bare training layout (root.js).
const Root = createRoot({ boot, store, renderBootError });

// Spec 53 3.3: a gated user is kept on the training pages. Installed before
// the router's first navigation, which runs before /users/me answers; the
// boot below moves them once it knows who this is.
const isTrainingGated = () => store.getters.trainingGated;
installTrainingGuard(router, isTrainingGated);

const app = createApp(Root);
app.config.globalProperties.$http = axios;
app.use(router);
app.use(store);
// Pinia beside Vuex: the live-map (tracking) store came in with the /tracking
// page, which was a separate Vue 3 app before this one was, and is a Pinia
// store. Both coexist on one app; nothing else should start a second state
// library without a reason written here.
app.use(createPinia());

// Error telemetry before anything below can throw: the pdf.js-worker class of
// failure (browser-only, server logs all 200s) must be reported, and that
// includes failures in this very boot sequence. The Sentry Vue integration
// installs onto the (not yet mounted) app; window.onerror and unhandled
// rejections are captured regardless. No-op unless a DSN resolves (see
// ui/src/config/telemetry.js).
initTelemetry({ app });

// At the training start page nothing boots (it redirects, or says why not),
// so it never asks who the user is: a promise that never settles.
(trainingStart ? new Promise(() => {}) : axios.get('/users/me'))
  .then(async function (me) {
    store.commit('setUser', me.data);
    store.dispatch('fetchOrganization').catch(() => {});
    boot.user = me.data;
    await settleTrainingGate(router, isTrainingGated);
    app.mount('#app');
  })
  .catch(err => {
    if (err.response && err.response.status === 401) {
      return (window.location = loginUrl());
    }
    // Spec 25 section 3.6, demo host only (header.vue's demo gate, and for its
    // reason). A visitor's tenant is reclaimed when they stop using it; their
    // cookie then names a user that no longer exists. That answers 401 now
    // (AUTH_USER_NOT_FOUND, 2026-10-14) and goes to login above; this stays
    // for any other boot failure on the demo, so the boot error below is never
    // the end of a prospect's demo. Back through the lease instead, keeping
    // whatever tour their link asked for.
    if (window.location.hostname === 'demo.ladeway.com') {
      return (window.location = loginUrl());
    }
    boot.error = true;
    app.mount('#app');
  });
